import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaUser, FaEnvelope, FaLock, FaFutbol, FaEye, FaEyeSlash, FaCheck, FaTimes } from 'react-icons/fa';
import toast from 'react-hot-toast';
import Button from '../components/UI/Button';
import { useAuth } from '../context/AuthContext';
function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({
    pseudo: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({});
  const [acceptedPolicy, setAcceptedPolicy] = useState(false);

  // Validations
  const validations = {
    pseudo: form.pseudo.length >= 3 && form.pseudo.length <= 20,
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email),
    password: form.password.length >= 6,
    confirmPassword: form.password === form.confirmPassword && form.confirmPassword !== '',
  };

  const isValid = Object.values(validations).every(Boolean) && acceptedPolicy;

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
  };

  const ValidationIcon = ({ field }) => {
    if (!touched[field]) return null;
    return validations[field]
      ? <FaCheck size={12} className="text-qfl-green" />
      : <FaTimes size={12} className="text-qfl-red" />;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) {
      setTouched({ pseudo: true, email: true, password: true, confirmPassword: true });
      if (!acceptedPolicy) toast.error('Tu dois accepter la politique de confidentialité');
      else toast.error('Corrige les erreurs avant de continuer');
      return;
    }

    setLoading(true);
    try {
      await register(form.pseudo, form.email, form.password);
      toast.success('Compte créé ! Bienvenue dans la League !');
      navigate('/');
    } catch (err) {
      const msg = err?.response?.data?.message || 'Erreur lors de l\'inscription';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="app-container flex flex-col min-h-screen"
      style={{
        background: 'radial-gradient(ellipse at top, #1a4a2e 0%, #0d2b1a 70%)',
      }}
    >
      {/* Header */}
      <div className="flex-shrink-0 h-24 flex flex-col items-center justify-end pb-3">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200 }}
          className="w-16 h-16 rounded-full bg-qfl-card border-2 border-qfl-gold flex items-center justify-center mb-2"
          style={{ boxShadow: '0 0 20px rgba(240,180,41,0.3)' }}
        >
          <FaFutbol size={28} className="text-qfl-gold" />
        </motion.div>
        <h1
          className="text-2xl font-black uppercase tracking-widest text-qfl-gold"
          style={{ fontFamily: "'Rajdhani', sans-serif" }}
        >
          OM QUIZ
        </h1>
      </div>

      {/* Formulaire */}
      <motion.div
        className="flex-1 flex flex-col px-6 pt-4 pb-10 overflow-y-auto"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <h2
          className="text-xl font-bold text-white mb-5 text-center"
          style={{ fontFamily: "'Rajdhani', sans-serif" }}
        >
          CRÉER UN COMPTE
        </h2>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
          {/* Pseudo */}
          <div>
            <div className="relative">
              <FaUser size={13} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="text"
                placeholder="Pseudo (3-20 caractères)"
                value={form.pseudo}
                onChange={(e) => handleChange('pseudo', e.target.value)}
                onBlur={() => handleBlur('pseudo')}
                className={`form-input pl-10 pr-10 ${
                  touched.pseudo && !validations.pseudo ? 'border-qfl-red/60' : ''
                } ${touched.pseudo && validations.pseudo ? 'border-qfl-green/60' : ''}`}
                maxLength={20}
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2">
                <ValidationIcon field="pseudo" />
              </span>
            </div>
            {touched.pseudo && !validations.pseudo && (
              <p className="text-qfl-red text-xs mt-1 ml-1">3 à 20 caractères requis</p>
            )}
          </div>

          {/* Email */}
          <div>
            <div className="relative">
              <FaEnvelope size={13} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                onBlur={() => handleBlur('email')}
                className={`form-input pl-10 pr-10 ${
                  touched.email && !validations.email ? 'border-qfl-red/60' : ''
                } ${touched.email && validations.email ? 'border-qfl-green/60' : ''}`}
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2">
                <ValidationIcon field="email" />
              </span>
            </div>
            {touched.email && !validations.email && (
              <p className="text-qfl-red text-xs mt-1 ml-1">Email invalide</p>
            )}
          </div>

          {/* Mot de passe */}
          <div>
            <div className="relative">
              <FaLock size={13} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Mot de passe (6 min.)"
                value={form.password}
                onChange={(e) => handleChange('password', e.target.value)}
                onBlur={() => handleBlur('password')}
                className={`form-input pl-10 pr-10 ${
                  touched.password && !validations.password ? 'border-qfl-red/60' : ''
                } ${touched.password && validations.password ? 'border-qfl-green/60' : ''}`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40"
              >
                {showPassword ? <FaEyeSlash size={13} /> : <FaEye size={13} />}
              </button>
            </div>
            {touched.password && !validations.password && (
              <p className="text-qfl-red text-xs mt-1 ml-1">Minimum 6 caractères</p>
            )}
          </div>

          {/* Confirmer mot de passe */}
          <div>
            <div className="relative">
              <FaLock size={13} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Confirmer le mot de passe"
                value={form.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                onBlur={() => handleBlur('confirmPassword')}
                className={`form-input pl-10 pr-10 ${
                  touched.confirmPassword && !validations.confirmPassword ? 'border-qfl-red/60' : ''
                } ${touched.confirmPassword && validations.confirmPassword ? 'border-qfl-green/60' : ''}`}
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2">
                <ValidationIcon field="confirmPassword" />
              </span>
            </div>
            {touched.confirmPassword && !validations.confirmPassword && (
              <p className="text-qfl-red text-xs mt-1 ml-1">Les mots de passe ne correspondent pas</p>
            )}
          </div>

          {/* Politique de confidentialité */}
          <div className="flex items-center gap-2 mt-1 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <input
              type="checkbox"
              id="accept-policy"
              checked={acceptedPolicy}
              onChange={(e) => setAcceptedPolicy(e.target.checked)}
              style={{ width: 18, height: 18, accentColor: '#f0b429', cursor: 'pointer', flexShrink: 0 }}
            />
            <label htmlFor="accept-policy" className="text-xs text-white/60 leading-relaxed cursor-pointer" style={{ fontFamily: "'Inter', sans-serif" }}>
              J'accepte la{' '}
              <a
                href="https://omquizfoot.dekanisoftware.com/politique-confidentialite.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-qfl-gold hover:underline font-semibold"
                onClick={(e) => e.stopPropagation()}
              >
                politique de confidentialité
              </a>
            </label>
          </div>

          <Button
            type="submit"
            variant="gold"
            size="lg"
            fullWidth
            loading={loading}
            disabled={!isValid}
            className="mt-2"
          >
            S'INSCRIRE
          </Button>
        </form>

        <p className="text-center text-white/50 text-sm mt-5">
          Déjà un compte ?{' '}
          <Link
            to="/login"
            className="text-qfl-gold font-bold hover:text-yellow-400 transition-colors"
          >
            Se connecter
          </Link>
        </p>
      </motion.div>
    </div>
  );
}

export default Register;
